import React, { Dispatch, SetStateAction } from 'react';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { ManagePaymentMethodsSidebar } from '@css-xp/base/common-sidebars';
import { useQuery } from '@apollo/client';
import {
  FetchInvoiceGroupPpcQuery,
  PaymentMethodType,
  PaymentMethod,
  FetchInvoiceGroupPpcQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { fetchInvoiceGroupQueryPPC } from '@css-xp/ppc-billing-profile-page';
import { DEFAULT_INFLIGHT_BANNER_MESSAGE, failureMessage } from '@css-xp/ppc-common';
import { updateDefaultPaymentMethod } from '@atlassian/temp-proxies';
import './change-payment-method-modal.scss';

interface Props {
  isOpen: boolean;
  invoiceGroupId: string;
  transactionAccountId: string;
  onClose: () => void;
  onSuccess: () => void;
  setSelectedPaymentMethod: Dispatch<SetStateAction<PaymentMethod | null>>;
  setIsPaymentMethodLoading: Dispatch<SetStateAction<boolean>>;
  isPaypalSupportEnabled?: boolean;
  shouldShowACHPaymentMethod?: boolean;
}

const successStateProps = {
  header: 'Update successful',
  description: 'Payment method updated.',
};
const errorStateProps = {
  header: 'Update unsuccessful',
  description: failureMessage(),
};

export const ChangePaymentMethodModal = ({
  invoiceGroupId,
  isOpen,
  transactionAccountId,
  onClose,
  onSuccess,
  setSelectedPaymentMethod,
  setIsPaymentMethodLoading,
  isPaypalSupportEnabled,
  shouldShowACHPaymentMethod,
}: Props) => {
  const { data: invoiceGroupDetails, loading: invoiceGroupLoading, refetch: refetchBillingProfile } = useQuery<
    FetchInvoiceGroupPpcQuery,
    FetchInvoiceGroupPpcQueryVariables
  >(fetchInvoiceGroupQueryPPC, {
    variables: {
      id: invoiceGroupId,
      txaId: transactionAccountId,
    },
  });

  React.useEffect(() => {
    setIsPaymentMethodLoading(invoiceGroupLoading);
  }, [invoiceGroupLoading, setIsPaymentMethodLoading]);

  React.useEffect(() => {
    if (invoiceGroupDetails?.invoiceGroup?.defaultPaymentMethod) {
      setSelectedPaymentMethod(invoiceGroupDetails?.invoiceGroup?.defaultPaymentMethod);
    }
  }, [invoiceGroupDetails?.invoiceGroup, refetchBillingProfile, setSelectedPaymentMethod]);

  let allPaymentMethods = [];
  let defaultPaymentMethodDetails;

  if (!invoiceGroupLoading) {
    //@ts-ignore
    const { defaultPaymentMethod, transactionAccount } = invoiceGroupDetails?.invoiceGroup;
    allPaymentMethods = transactionAccount?.availablePaymentMethods?.nodes?.filter(
      (node: PaymentMethod) => node.type !== PaymentMethodType.DEFERRED
    );
    defaultPaymentMethodDetails = defaultPaymentMethod;
  }

  return (
    <ModalTransition>
      {isOpen && (
        <Modal
          onClose={onClose}
          testId="change-payment-method-modal"
          shouldCloseOnOverlayClick={false}
          shouldScrollInViewport
        >
          <div className="change-payment-method-modal">
            <ManagePaymentMethodsSidebar
              transactionAccountId={transactionAccountId}
              allPaymentMethods={allPaymentMethods}
              defaultPaymentMethodId={defaultPaymentMethodDetails?.id}
              defaultPaymentMethodType={defaultPaymentMethodDetails?.type as string}
              refetch={refetchBillingProfile}
              invoiceGroupId={invoiceGroupId as string}
              name={invoiceGroupDetails?.invoiceGroup?.name as string}
              submitAction={updateDefaultPaymentMethod}
              isTermPaymentMethodAllowed={false}
              showAdditionalPaymentMethods={false}
              errorStateProps={errorStateProps}
              successStateProps={successStateProps}
              showDisclaimer={false}
              heading={'Change payment method'}
              inflightBannerMessage={DEFAULT_INFLIGHT_BANNER_MESSAGE}
              linkedEntitlementHeading={'This change will apply to these linked subscriptions:'}
              isPaypalSupportEnabled={isPaypalSupportEnabled}
              onClose={onClose}
              onSuccess={onSuccess}
              hideTermPaymentWarningMessage={true}
              shouldShowACHPaymentMethod={shouldShowACHPaymentMethod}
            />
          </div>
        </Modal>
      )}
    </ModalTransition>
  );
};
